import React from 'react';
import ReactDOM from 'react-dom';
import {PropTypes} from 'prop-types';
import moment from 'moment-timezone';
import Select from 'react-select';
import {BaseFormComponent} from '../../shared/baseFormComponent';
import {ErrorsBlockComponent} from '../../shared/errorsBlockComponent';
import {ButtonBusyIndicatorComponent} from '../../shared/buttonBusyIndicatorComponent';
import {Checkbox} from '../../shared/checkbox';
import {Link} from '../../shared/link';
import {SimpleTooltip} from '../../shared/tooltip';
import {ModalBase} from '../../shared/messageBoxComponent';
import {SetFocusOnFirstFormControl, AssignProperties, ToString, MakeApiCall, FormatString, PrepareError, CreateTimer, PreventDOMLock} from '../../shared/utils';

import './styles.scss';

const AUTH_ACTION = {
    NONE: 0,
    LOGIN: 1,
    REGISTER: 2,
    WAIT_OTP: 3,
    CHECK_OTP: 4
}

class LoginForm extends BaseFormComponent {
    constructor(props, ...others) {
        super(props, ...others);

        this._valueHandler.onBlurOptions = {
            'Email': BaseFormComponent.EmailOptions
        }
    }

    componentDidMount() {
        const self = this;
        if (Boolean(self.props.isSetFocus)) {
            SetFocusOnFirstFormControl(self, true);
        }
    }

    render() {
        const self = this;
        const {data, disabled, errors, strings} = self.props;
        const formData = {...self.constructor.defaultProps.data, ...data};

        return (
            <div id="login-block">
                <div className="form-group newstyle">
                    <div className={'input-group ' + self.getControlClass('Email', errors)} disabled={disabled} >
                        <input 
                            type="text" 
                            id="login-email"
                            name="Email" 
                            autoComplete="email"
                            onChange={self.handleInputChange}
                            onBlur={self.handleInputChange}
                            className={'form-control ' + self.getControlClass('Email', errors)} 
                            value={formData.Email} 
                            disabled={disabled} 
                        />
                        <div className="input-group-append">
                            <span className="input-group-text"><span className="sbkico-envelope"/></span>
                        </div>
                        <label htmlFor="login-email"><span className="text-truncate">{strings.PH_Email}</span></label>
                        <div className="invalid-feedback">{self.getControlError('Email', errors)}</div>
                    </div>
                </div>
                <div className="form-group newstyle">
                    <div className={'input-group ' + self.getControlClass('Password', errors)} disabled={disabled} >
                        <input 
                            type="password" 
                            id="login-password"
                            name="Password" 
                            onChange={self.handleInputChange}
                            onBlur={self.handleInputChange}
                            className={'form-control ' + self.getControlClass('Password', errors)} 
                            value={formData.Password} 
                            disabled={disabled} 
                        />
                        <div className="input-group-append">
                            <span className="input-group-text"><span className="sbkico-lock"/></span>
                        </div>
                        <label htmlFor="login-password"><span className="text-truncate">{strings.PH_Password}</span></label>
                        <div className="invalid-feedback">{self.getControlError('Password', errors)}</div>
                    </div>
                </div>
            </div>
        );        
    }
}
LoginForm.propTypes = {
    strings: PropTypes.object.isRequired,
    data: PropTypes.object,
    isSetFocus: PropTypes.bool,
    disabled: PropTypes.bool,
    errors: PropTypes.object
}
LoginForm.defaultProps = {
    isSetFocus: false,
    disabled: false,
    errors: null,
    data: {
        Email: '',
        Password: ''
    }
}

class RegisterForm extends BaseFormComponent{
    constructor(props, ...others) {
        super(props, ...others);
        this.state = {
            countries: this.mapCountries(props.countries),
            timeZones: this.mapTimeZones(props.timeZones)
        }

        this.handleTimeZoneButtonClick = this.handleTimeZoneButtonClick.bind(this);
        this.handleTimeZoneChange = this.handleTimeZoneChange.bind(this);
        this.handleCountryChange = this.handleCountryChange.bind(this);

        this.countrySelectRef = React.createRef();
        this.timeZoneSelectRef = React.createRef();

        this._valueHandler.onBlurOptions = {
            'Name': BaseFormComponent.OPTIONS.AllTrim,
            'Email': BaseFormComponent.EmailOptions,
        }
    }

    mapCountries(countriesList) {
        let result = [];

        if (Boolean(countriesList)) {
            result = countriesList
                .map(p => {
                    return {value: p.Key, label: p.Value};
                })
                .sort((p1, p2) => {
                    return ToString(p1.label).localeCompare(p2.label);
                });
        }

        return result;
    }

    mapTimeZones(timeZonesList) {
        let result = [];

        if (Boolean(timeZonesList)) {
            const utcNow = moment.utc();

            result = timeZonesList
                .map(p => {
                    return {
                        value: p.Id,
                        label: '(UTC' + utcNow.clone().tz(p.Id).format('Z') + ') ' + p.Name,
                        ids: p.Ids.slice()
                    }
                })
                .sort((p1, p2) => {
                    return ToString(p1.label).localeCompare(p2.label);
                });
        }

        return result;
    }

    componentDidMount() {
        const self = this;
        self._isMounted = true;
        if (Boolean(self.props.isSetFocus)) {
            SetFocusOnFirstFormControl(self, true);
        }
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    componentDidUpdate(prevProps) {
        const self = this;
        const props = self.props;
        let newState = null;
        if (props.timeZones != prevProps.timeZones) {
            newState = {...newState, timeZones: self.mapTimeZones(props.timeZones)};
        }
        if (Boolean(newState)) {
            self.setState(newState);
        }
    }

    handleTimeZoneButtonClick() {
        const self = this;
        const {timeZones} = self.state;

        let timeZoneId = moment.tz.guess(true);

        if (!timeZones.some(p => p.value == timeZoneId)) {
            const timeZone = timeZones.find(p => p.ids.some(id => id == timeZoneId));
            if (Boolean(timeZone)) {
                timeZoneId = timeZone.value;
            }
        }

        PreventDOMLock(() => {
            if (self._isMounted) {
                self.handleTimeZoneChange({value: timeZoneId});
            }
        });
    }

    handleTimeZoneChange(option) {
        const self = this;
        const value = Boolean(option) ? option.value : '';
        self.props.onChange('TimeZoneId', value);
        if (self.timeZoneSelectRef.current) {
            $('input', ReactDOM.findDOMNode(self.timeZoneSelectRef.current)).val(value).trigger('change');
        }
    }

    handleCountryChange(option) {
        const self = this;
        const value = Boolean(option) ? option.value : '';
        self.props.onChange('CountryId', value);
        if (self.countrySelectRef.current) {
            $('input', ReactDOM.findDOMNode(self.countrySelectRef.current)).val(value).trigger('change');
        }
    }

    render() {
        const self = this;
        const {data, isCountryRequired, disabled, errors, strings} = self.props;
        const {countries, timeZones} = self.state;

        const formData = {...self.constructor.defaultProps.data, ...data};
        const selectedCountry = countries.find(p => p.value == formData.CountryId);
        let selectedTimeZone = timeZones.find(p => p.value == formData.TimeZoneId);
        if (!Boolean(selectedTimeZone)) {
            selectedTimeZone = timeZones.find(p => p.ids.some(id => id == formData.TimeZoneId));
        }

        return (
            <div id="register-block">
                <div className="form-group newstyle">
                    <div className={'input-group ' + self.getControlClass('Name', errors)} disabled={disabled} >
                        <input 
                            type="text" 
                            id="register-fullname"
                            name="Name" 
                            autoComplete="name"
                            // onChange={(e) => self.handleInputChange(e, {doLTrim: true})}
                            onChange={self.handleInputChange}
                            onBlur={self.handleInputChange}
                            className={'form-control ' + self.getControlClass('Name', errors)} 
                            value={formData.Name} 
                            disabled={disabled} 
                        />
                        <div className="input-group-append">
                            <span className="input-group-text"><span className="sbkico-user"/></span>
                        </div>
                        <label htmlFor="register-fullname"><span className="text-truncate">{strings.PH_Name}</span></label>
                        <div className="invalid-feedback">{self.getControlError('Name', errors)}</div>
                    </div>
                </div>
                <div className="form-group newstyle">
                    <div className={'input-group ' + self.getControlClass('Email', errors)} disabled={disabled} >
                        <input 
                            type="text" 
                            id="register-email"
                            name="Email" 
                            autoComplete="email"
                            // onChange={e => self.handleInputChange(e, BaseFormComponent.EmailOptions)}
                            onChange={self.handleInputChange}
                            onBlur={self.handleInputChange}
                            className={'form-control ' + self.getControlClass('Email', errors)} 
                            value={formData.Email} 
                            disabled={disabled} 
                        />
                        <div className="input-group-append">
                            <span className="input-group-text"><span className="sbkico-envelope"/></span>
                        </div>
                        <label htmlFor="register-email"><span className="text-truncate">{strings.PH_Email}</span></label>
                        <div className="invalid-feedback">{self.getControlError('Email', errors)}</div>
                    </div>
                </div>
                <div className="form-group newstyle">
                    <div className={'input-group ' + self.getControlClass('Password', errors)} disabled={disabled} >
                        <input 
                            type="password" 
                            id="register-password"
                            name="Password" 
                            autoComplete="new-password"
                            onChange={self.handleInputChange}
                            onBlur={self.handleInputChange}
                            className={'form-control ' + self.getControlClass('Password', errors)} 
                            value={formData.Password} 
                            disabled={disabled} 
                        />
                        <div className="input-group-append">
                            <span className="input-group-text"><span className="sbkico-lock"/></span>
                        </div>
                        <label htmlFor="register-password"><span className="text-truncate">{strings.PH_Password}</span></label>
                        <div className="invalid-feedback">{self.getControlError('Password', errors)}</div>
                    </div>
                </div>
                {Boolean(isCountryRequired) &&
                    <div className="form-group newstyle">
                        <div className={'input-group ' + self.getControlClass('CountryId', errors)} disabled={disabled} >
                            <Select 
                                ref={self.countrySelectRef}
                                inputId="CountryId" 
                                className={'selectex ' + self.getControlClass('CountryId', errors)}
                                value={selectedCountry} 
                                placeholder={strings.PH_Country}
                                isDisabled={disabled} 
                                isSearchable
                                classNamePrefix="selectex"
                                onChange={self.handleCountryChange}
                                options={countries}
                                components={{IndicatorsContainer: () => null}}
                                noOptionsMessage={() => ''}
                            />
                        </div>
                        <label htmlFor="CountryId"><span className="text-truncate">{strings.F_Country}</span></label>
                        <div className="invalid-feedback">{self.getControlError('CountryId', errors)}</div>
                    </div>
                }
                <div className="form-group newstyle">
                    <div className={'input-group ' + self.getControlClass('TimeZoneId', errors)} disabled={disabled} >
                        <Select 
                            ref={self.timeZoneSelectRef}
                            inputId="TimeZoneId" 
                            className={'selectex ' + self.getControlClass('TimeZoneId', errors)}
                            value={selectedTimeZone} 
                            isDisabled={disabled} 
                            isSearchable
                            classNamePrefix="selectex"
                            onChange={self.handleTimeZoneChange}
                            options={timeZones}
                            components={{IndicatorsContainer: () => null}}
                            noOptionsMessage={() => ''}
                        />
                        <div className="input-group-append">
                            <SimpleTooltip title={strings.TT_TimeZoneAutoSelect} placement="right">
                                <button 
                                    className="btn btn-primary newstyle" 
                                    type="button" 
                                    disabled={disabled} 
                                    onClick={self.handleTimeZoneButtonClick} 
                                >
                                    <span className="sbkico-globe-time text-white" />
                                </button>
                            </SimpleTooltip>
                        </div>                        
                        <label htmlFor="TimeZoneId"><span className="text-truncate">{strings.PH_TimeZone}</span></label>
                        <div className="invalid-feedback">{self.getControlError('TimeZoneId', errors)}</div>
                    </div>
                </div>                
            </div>
        );
    }
}
RegisterForm.propTypes = {
    strings: PropTypes.object.isRequired,
    data: PropTypes.object,
    isSetFocus: PropTypes.bool,
    email: PropTypes.string,
    password: PropTypes.string,
    name: PropTypes.string,
    countryId: PropTypes.string,
    timeZoneId: PropTypes.string,
    disabled: PropTypes.bool,
    errors: PropTypes.object,
    timeZones: PropTypes.array.isRequired,
    countries: PropTypes.array,
    isCountryRequired: PropTypes.bool
}
RegisterForm.defaultProps = {
    isSetFocus: false,
    disabled: false,
    errors: null,
    countries: null,
    isCountryRequired: false,
    data: {
        Email: '',
        Name: '',
        Password: '',
        CountryId: '',
        TimeZoneId: ''
    }
}

export class AuthFormComponent extends React.Component {
    constructor(props, ...others) {
        super(props, ...others);
        this.strings = {...this.constructor.defaultProps.strings};
        AssignProperties(this.strings, props.strings);

        this._OTPDialogRef = React.createRef();

        this.state = {
            action: AUTH_ACTION.NONE, 
            loginErrors: null, 
            registerErrors: null,
            socialErrors: props.socialErrors,
            twoFAErrors: null,
            reCaptchaAPILoaded: false,
            loginData: {
                Email: '', 
                Password: ''
            },
            registerData: {
                Name: '', 
                Email: '', 
                Password: '', 
                TimeZoneId: props.timeZoneId || moment.tz.guess(true), 
                CountryId: props.countryId || ''
            },
            OTPToken: '',
            OTPMessage: '',
            OTPErrorMessage: '',
            OTP: ''
        };

        this.onLoginDataChanged = this.onLoginDataChanged.bind(this);
        this.onRegisterDataChanged = this.onRegisterDataChanged.bind(this);
        this.handleOTPChanged = this.handleOTPChanged.bind(this);
        this.handleLoginSubmit = this.handleLoginSubmit.bind(this);
        this.handleRegisterSubmit = this.handleRegisterSubmit.bind(this);
        this.handleSocialAuthClick = this.handleSocialAuthClick.bind(this);
        this.handleIsAgreeChange = this.handleIsAgreeChange.bind(this);
        this.doOk = this.doOk.bind(this);
        this.doLogin = this.doLogin.bind(this);
        this.doRegister = this.doRegister.bind(this);
        this.doSocialAuth = this.doSocialAuth.bind(this);
        this.renderSocialButton = this.renderSocialButton.bind(this);

        this.handleOTPDialogCloseClick = this.handleOTPDialogCloseClick.bind(this);
        this.handleOTPDialogClose = this.handleOTPDialogClose.bind(this);

        this.getReCaptchaToken = this.getReCaptchaToken.bind(this);
        this.reCaptchaTimer = CreateTimer(200);
        this.checkReCaptchaLoaded = this.checkReCaptchaLoaded.bind(this);
    }

    checkReCaptchaLoaded() {
        const self = this;

        self.reCaptchaTimer.stop();

        const grecaptchaApi = window['grecaptcha'];
        if (Boolean(grecaptchaApi) && !self.state.reCaptchaAPILoaded) {
            grecaptchaApi.ready(
                () => {
                    if (self._isMounted) {
                        self.setState({reCaptchaAPILoaded: true});
                    }
                }
            )
        }

        if (!Boolean(grecaptchaApi) && self._isMounted) {
            self.reCaptchaTimer.start();
        }
    }

    componentDidMount() {
        const self = this;
        self._isMounted = true;

        self.reCaptchaTimer.onTimer = self.checkReCaptchaLoaded;
        self.checkReCaptchaLoaded();
    }

    componentWillUnmount() {
        this._isMounted = false;
        self.reCaptchaTimer.stop();
    }

    getReCaptchaToken() {
        const self = this;
        const {reCaptchaSiteKey} = self.props;
        if (!Boolean(reCaptchaSiteKey)) {
            return Promise.resolve('');
        }

        const grecaptchaApi = window['grecaptcha'];
        if (!self.state.reCaptchaAPILoaded) {
            return Promise.reject('ReCAPTCHA API was not loaded.');
        }
        else {
            return new Promise((resolve, reject) => {
                try {
                    grecaptchaApi.execute(reCaptchaSiteKey, {action: 'homepage'})
                        .then(token => {
                            resolve(token);
                        });
                }
                catch (error) {
                    //reject(error);
                    reject('ReCAPTCHA API error.');
                }
            })
        }
    }

    onLoginDataChanged(name, value) {
        const self = this;
        if (self._isMounted && (self.state.loginData[name] != value)) {
            self.setState({loginData: {...self.state.loginData, [name]: value}});
        }
    }

    onRegisterDataChanged(name, value) {
        const self = this;
        if (self._isMounted && (self.state.registerData[name] != value)) {
            self.setState({registerData: {...self.state.registerData, [name]: value}});
        }
    }

    handleOTPChanged(e) {
        const self = this;
        const value = ToString(e.target.value);

        PreventDOMLock(() => {
            if (self._isMounted && (self.state.OTP != value)) {
                self.setState({OTP: value});
            }
        });
    }

    handleLoginSubmit(event) {
        event.preventDefault();
        const self = this;

        PreventDOMLock(self.doLogin);
    }

    handleRegisterSubmit(event) {
        event.preventDefault();
        const self = this;

        PreventDOMLock(self.doRegister);
    }

    handleSocialAuthClick(event) {
        const self = this;
        const providerId = event.currentTarget.getAttribute('data-id');

        if (Boolean(providerId)) {
            PreventDOMLock(() => {
                self.doSocialAuth(providerId);
            });
        }
    }

    handleIsAgreeChange(event) {
        const self = this;
        const value = event.target.checked;

        PreventDOMLock(() => {
            self.onRegisterDataChanged("IsAgree", value);
        });
    }

    doOk(url) {
        const self = this;
        if (self._isMounted) {
            url = (ToString(url) || ToString(self.props.returnUrl)).trim();
            window.location.assign(url);

            //If URL is link to resource (file for example), then redirecting to "Home" page after short pause
            //setTimeout(() => window.location.assign('/'), 4000);
        }
    }

    doLogin() {
        const self = this;
        const {action, OTP, OTPToken, loginData} = self.state;
        const {loginApiUrl, verificationToken, returnUrl} = self.props;

        if (!Boolean(loginApiUrl) || !self._isMounted) {
            return;
        }

        let resetPasswordOnError = false;

        const data = {...loginData, ReturnUrl: returnUrl};
        if (action == AUTH_ACTION.WAIT_OTP) {
            data.OTP = ToString(OTP).trim();
            data.OTPToken = OTPToken;

            resetPasswordOnError = true;
        }

        const newAction = (action == AUTH_ACTION.WAIT_OTP)
            ? AUTH_ACTION.CHECK_OTP
            : AUTH_ACTION.LOGIN;

        new Promise(resolve => {
            self.setState({action: newAction, socialErrors: null, loginErrors: null, registerErrors: null, OTPErrorMessage: ''}, resolve);
        })
        .then(() => {
            if (self._isMounted) {
                return self.getReCaptchaToken();
            }
        })
        .then(token => {
            if (self._isMounted) {
                data.ReCaptchaToken = token;

                return MakeApiCall(
                    $.post(
                        {
                            url: loginApiUrl,
                            headers: {RequestVerificationToken: verificationToken},
                            data: data,
                            dataType: 'json',
                            cache: false
                        }
                    ));
            }
        })
        .then(responseData => {
            if (self._isMounted) {
                if (responseData.OTPToken) {
                    const newState = {
                        action: AUTH_ACTION.WAIT_OTP, 
                        OTP: '', 
                        OTPToken: responseData.OTPToken, 
                        OTPErrorMessage: responseData.OTPErrorMessage
                    }

                    if (Boolean(responseData.OTPMessage)) {
                        newState.OTPMessage = responseData.OTPMessage;
                    }

                    self.setState(newState);
                }
                else {
                    self.doOk(responseData.SuccessUrl);
                }
            }
        })
        .catch(errors => {
            if (self._isMounted) {
                const newState = {action: AUTH_ACTION.NONE, loginErrors: PrepareError(errors)};

                if (resetPasswordOnError) {
                    newState.loginData = {...self.state.loginData, Password: ''};
                }
                self.setState(newState);
            }
        });
    }

    doRegister() {
        const self = this;
        const {registerApiUrl, verificationToken, returnUrl} = self.props;

        if (!Boolean(registerApiUrl) || !self._isMounted) {
            return;
        }

        const data = {...self.state.registerData, ReturnUrl: returnUrl};

        new Promise(resolve => {
            self.setState({action: AUTH_ACTION.REGISTER, socialErrors: null, loginErrors: null, registerErrors: null}, resolve);
        })
        .then(() => {
            if (self._isMounted) {
                return self.getReCaptchaToken();
            }
        })
        .then(token => {
            if (self._isMounted) {
                data.ReCaptchaToken = token;

                return MakeApiCall(
                    $.post(
                        {
                            url: registerApiUrl,
                            headers: {RequestVerificationToken: verificationToken},
                            data: data,
                            dataType: 'json',
                            cache: false
                        }
                    ));
            }
        })
        .then(responseData => {
            self.doOk(responseData.SuccessUrl);
        })
        .catch(errors => {
            if (self._isMounted) {
                self.setState({action: AUTH_ACTION.NONE, registerErrors: PrepareError(errors)});
            }
        });
    }

    doSocialAuth(providerId) {
        const self = this;
        const {socialAuthApiUrl, verificationToken, returnUrl} = self.props;

        if (!Boolean(socialAuthApiUrl) || !self._isMounted) {
            return;
        }

        const data = {Provider: providerId, ReturnUrl: returnUrl};

        new Promise(resolve => {
            if (self._isMounted) {
                self.setState({action: providerId, socialErrors: null, loginErrors: null, registerErrors: null}, resolve);
            }
            else {
                reject(null);
            }
        })
        .then(() => {
            if (self._isMounted) {
                return MakeApiCall(
                    $.post(
                        {
                            url: socialAuthApiUrl,
                            headers: {RequestVerificationToken: verificationToken},
                            data: data,
                            dataType: 'json',
                            cache: false
                        }
                    ));
            }
        })
        .then(responseData => {
            if (self._isMounted) {
                self.doOk(responseData.SuccessUrl);
            }
        })
        .catch(errors => {
            if (self._isMounted) {
                self.setState({action: AUTH_ACTION.NONE, socialErrors: errors})
            }
        });
    }

    renderAgreeBlock() {
        const self = this;
        const {action, registerData} = self.state;
        const {termsUrl, privacyUrl} = self.props;
        const strings = self.strings;

        const links = [];
        if (Boolean(termsUrl)) {
            links.push(<Link key="terms" id="terms-link" to={termsUrl} target="_blank" className="agree-link text-nowrap">{strings.A_Terms}</Link>);
        }
        if (Boolean(privacyUrl)) {
            links.push(<Link key="privacy" id="privacy-link" to={privacyUrl} target="_blank" className="agree-link text-nowrap">{strings.A_Privacy}</Link>);
        }
     
        if (links.length == 0) {
            return null;
        }

        const labelItems = [<span key="agree">{strings.M_AgreeTo}&nbsp;</span>];

        links.forEach((item, index) => {
            if (index > 0) {
                labelItems.push(<span key={'and' + index}>{` ${strings.M_And} `}</span>);
            }
            labelItems.push(item);
        });

        return (
            <div className="terms-block">
                <Checkbox 
                    key="mamec"
                    className="d-inline"
                    value={registerData.IsAgree} 
                    onChange={self.handleIsAgreeChange}
                    disabled={action != AUTH_ACTION.NONE}
                    name="IsAgree"
                    id="IsAgree"
                    label={labelItems}
                />
            </div>
        )
    }

    renderSocialButton(providerInfo) {
        const self = this;
        const {action} = self.state;

        return (
            <div 
                key={providerInfo.ProviderName}
                className="col"
            >
                <button
                    type="button"
                    className={providerInfo.ButtonClassName + ' btn-social newstyle'}
                    disabled={action !== AUTH_ACTION.NONE}
                    data-id={providerInfo.ProviderName}
                    onClick={self.handleSocialAuthClick}
                >
                    {FormatString(self.strings.M_LogInWith, providerInfo.DisplayName)}<ButtonBusyIndicatorComponent visible={action == providerInfo.ProviderName} />
                </button>
            </div>
        )
    }

    handleOTPDialogCloseClick(e) {
        const self = this;
        ModalBase.HideModal(self._OTPDialogRef.current);
    }

    handleOTPDialogClose() {
        const self = this;

        if (self._isMounted) {
            self.setState({action: AUTH_ACTION.NONE});
        }
    }

    renderOTPDialog() {
        const self = this;
        const {action, OTP, OTPMessage, OTPErrorMessage} = self.state;

        if ([AUTH_ACTION.WAIT_OTP, AUTH_ACTION.CHECK_OTP].indexOf(action) < 0) {
            return null;
        }

        const strings = self.strings;

        return (
            <ModalBase 
                id="Auth.dlgOTP"
                ref={self._OTPDialogRef}
                className="newstyle"
                title={strings.DLG_OTP}
                body={
                    <div>
                        <p>{OTPMessage}</p>
                        <form onSubmit={self.handleLoginSubmit}>
                            <div className="form-group newstyle">
                                <input 
                                    type="text"
                                    className={'form-control' + (Boolean(OTPErrorMessage) ? ' is-invalid' : '')}
                                    value={OTP}
                                    autoComplete="one-time-code"
                                    onChange={self.handleOTPChanged}
                                    readOnly={action != AUTH_ACTION.WAIT_OTP}
                                />
                                <label>Enter OTP</label>
                                <div className="invalid-feedback">{OTPErrorMessage}</div>
                            </div>
                            <div className="form-group">
                                <button 
                                    className="btn btn-primary newstyle w-100" 
                                    type="submit"
                                    disabled={(action != AUTH_ACTION.WAIT_OTP) || !Boolean(OTP)}
                                >
                                    {strings.BTN_SubmitOTP}
                                    <ButtonBusyIndicatorComponent visible={action == AUTH_ACTION.CHECK_OTP} />
                                </button>
                            </div>
                        </form>
                    </div>
                }
                footer={
                    <div className="w-100 d-flex">
                        {/* <div className="ml-auto">
                            <div className="form-buttons">
                                <button 
                                    className="btn btn-primary newstyle btn-sm" 
                                    onClick={self.handleLoginSubmit}
                                    disabled={(action != AUTH_ACTION.WAIT_OTP) || !Boolean(OTP)}
                                >
                                    {strings.BTN_SubmitOTP}
                                    <ButtonBusyIndicatorComponent visible={action == AUTH_ACTION.CHECK_OTP} />
                                </button>
                                <button 
                                    className="btn btn-outline-secondary newstyle btn-sm" 
                                    onClick={self.handleOTPDialogCloseClick}
                                    disabled={action != AUTH_ACTION.WAIT_OTP}
                                >
                                    Close
                                </button>
                            </div>
                        </div> */}
                    </div>
                }
                visible
                allowClose={action == AUTH_ACTION.WAIT_OTP}
                onHide={self.handleOTPDialogClose}
                size="sm"
            />
        )
    }

    render() {
        const self = this;
        const {action, loginErrors, registerErrors, socialErrors, loginData, registerData, reCaptchaAPILoaded} = self.state;
        const {authMessage, loginButtonCaption, registerButtonCaption, recoverPasswordUrl, termsUrl, socialAuthApiUrl, isRegisterAuthAction, isCountryRequired, countries, timeZones, socialProviders, reCaptchaSiteKey} = self.props;
        const isDisabled = action !== AUTH_ACTION.NONE;
        const strings = self.strings;

        let recoverUrl = ToString(recoverPasswordUrl);
        if (Boolean(recoverUrl) && Boolean(loginData.Email)) {
            recoverUrl += '?email=' + encodeURIComponent(loginData.Email);
        }

        let isNotAgree = (Boolean(termsUrl) || Boolean(privacyUrl)) && !Boolean(registerData.IsAgree);
        const isAuthAllowed = !Boolean(reCaptchaSiteKey) || reCaptchaAPILoaded;

        return (
            <div className="auth-form">
                {self.renderOTPDialog()}
                {Boolean(authMessage) &&
                    <div className="pb-4 text-center fs-150 font-weight-semibold">
                        {authMessage}
                    </div>
                }
                <div className="row">
                    <div className="col-md-6">
                        <div className="card card-login">
                            <h2 className="card-header">{strings.LoginHeader}</h2>
                            <div className="card-body">
                                <ErrorsBlockComponent errors={loginErrors} unstyled excludedErrors="Email Password" className="text-danger form-group" />
                                <form 
                                    id="login-form" 
                                    name="LoginForm" 
                                    onSubmit={isAuthAllowed ? self.handleLoginSubmit : null}
                                >
                                    <LoginForm 
                                        strings={strings}
                                        data={loginData}
                                        disabled={isDisabled}
                                        onChange={self.onLoginDataChanged}
                                        errors={loginErrors}
                                        isSetFocus={!(isRegisterAuthAction)}
                                    />
                                    <div className="form-group form-buttons">
                                        {isAuthAllowed &&
                                            <button 
                                                id="login-button"
                                                type="submit" 
                                                className="btn btn-primary btn-lg newstyle" 
                                                disabled={isDisabled}
                                            >
                                                {loginButtonCaption}<ButtonBusyIndicatorComponent visible={action == AUTH_ACTION.LOGIN} />
                                            </button>
                                        }
                                        {Boolean(recoverUrl) && 
                                            <Link
                                                id="recover-link" 
                                                to={recoverUrl} 
                                                className="btn btn-link newstyle"
                                                disabled={isDisabled}
                                            >
                                                {strings.A_ForgotPassword}
                                            </Link>
                                        }
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>

                    <div className="col-md-6">
                        <div className="card card-register">
                            <h2 className="card-header">{strings.RegisterHeader}</h2>
                            <div className="card-body">
                                <ErrorsBlockComponent errors={registerErrors} unstyled excludedErrors="Name Email Password CountryId TimeZoneId" className="text-danger form-group" />
                                <form 
                                    id="register-form" 
                                    name="RegisterForm" 
                                    onSubmit={isAuthAllowed ? self.handleRegisterSubmit : null}
                                >
                                    <RegisterForm 
                                        strings={strings}
                                        data={registerData}
                                        countries={countries}
                                        timeZones={timeZones}
                                        disabled={isDisabled}
                                        onChange={self.onRegisterDataChanged}
                                        errors={registerErrors}
                                        isSetFocus={isRegisterAuthAction}
                                        isCountryRequired={isCountryRequired}
                                    />
                                    {self.renderAgreeBlock()}
                                    <div className="form-group form-buttons">
                                        {isAuthAllowed &&
                                            <button 
                                                id="register-button"
                                                type="submit" 
                                                className="btn btn-primary btn-lg newstyle" 
                                                disabled={isDisabled || isNotAgree}
                                            >
                                                {registerButtonCaption}<ButtonBusyIndicatorComponent visible={action == AUTH_ACTION.REGISTER} />
                                            </button>
                                        }
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
                {Boolean(socialProviders) && (socialProviders.length > 0) && Boolean(socialAuthApiUrl) &&
                    <div className={`socialblock socialblock-${socialProviders.length}`}>
                        <ErrorsBlockComponent errors={socialErrors} unstyled />
                        <h2>{strings.M_ContinueWithSocial}</h2>
                        <div className="row socialbuttons">
                            {socialProviders.map(self.renderSocialButton)}
                        </div>
                    </div>
                }
            </div>
        );
    }
}
AuthFormComponent.propTypes = {
    strings: PropTypes.object,
    verificationToken: PropTypes.string.isRequired,
    returnUrl: PropTypes.string,
    recoverPasswordUrl: PropTypes.string,
    termsUrl: PropTypes.string,
    privacyUrl: PropTypes.string,
    loginApiUrl: PropTypes.string.isRequired,
    socialAuthApiUrl: PropTypes.string,
    registerApiUrl: PropTypes.string.isRequired,
    timeZones: PropTypes.arrayOf(PropTypes.object).isRequired,
    countries: PropTypes.arrayOf(PropTypes.object),
    countryId: PropTypes.string,
    timeZoneId: PropTypes.string,
    socialProviders: PropTypes.arrayOf(PropTypes.object),
    reCaptchaSiteKey: PropTypes.string,

    authMessage: PropTypes.string,
    socialErrors: PropTypes.object,
    loginButtonCaption: PropTypes.string,
    registerButtonCaption: PropTypes.string,
    isRegisterAuthAction: PropTypes.bool,
    isCountryRequired: PropTypes.bool,
}
AuthFormComponent.defaultProps = {
    strings: {
        PH_Password: 'Password',
        PH_Email: 'Email',
        PH_Name: 'Name',
        PH_Country: 'Country',
        PH_TimeZone: 'Time Zone',
        LoginHeader: 'Log In',
        RegisterHeader: 'Register',
        TT_TimeZoneAutoSelect: 'Select Time Zone automatically',
        M_ContinueWithSocial: 'Or continue with a social network:',
        M_AgreeTo: 'I agree to',
        M_And: 'and',
        A_ForgotPassword: 'forgot password?',
        A_Terms: 'Terms & Conditions',
        A_Privacy: 'Privacy Policy',
        M_LogInWith: 'Log In with {0}',

        BTN_SubmitOTP: 'Continue',
        DLG_OTP: 'Authentication required',
    },
    authMessage: '',
    returnUrl: '',
    recoverPasswordUrl: '',
    termsUrl: '',
    privacyUrl: '',
    socialAuthApiUrl: '',
    countryId: '',
    timeZoneId: '',
    countries: null,
    socialProviders: null,
    loginButtonCaption: 'Log In',
    registerButtonCaption: 'Register',
    isRegisterAuthAction: false,
    isCountryRequired: false
}
